import { useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import { guardCubeSession } from '@/services/cube-sso';
import { CCN_HOME, clearJarvisSession, isCcnSession } from '@utils/auth-session';
import { impersonationUtils } from '@utils/impersonation';

const CUBE_GUARD_INTERVAL_MS = 5 * 60 * 1000;
// A single failed guard is usually MSAL noise (third-party cookies, a slow
// silent renewal). Only a run of failures means the CUBE session is gone.
const CUBE_GUARD_FAILURES_TO_LOGOUT = 3;
// Never log a user out in the first minute: the session was just created.
const CUBE_GUARD_GRACE_MS = 60 * 1000;

/** Sessions that came through CUBEforall — including PPJK users who chose that button. */
const isCubeSession = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const authData = localStorage.getItem('authData');
  const accessToken = localStorage.getItem('accessToken');
  const mfaEnabled = localStorage.getItem('mfaEnabled');

  if (!authData || !accessToken || mfaEnabled !== 'true') {
    return false;
  }

  try {
    const parsed = JSON.parse(authData);
    return parsed?.authSource === 'cubeforall' || parsed?.source === 'cubeforall' || parsed?.isCCNUser === true;
  } catch {
    return false;
  }
};

export default function CubeSessionGuard() {
  const failures = useRef(0);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;

    const expire = (message: string) => {
      const target = isCcnSession() ? `${CCN_HOME}/callback` : '/auth';
      clearJarvisSession();
      notifications.show({ title: 'Session Expired', message, color: 'yellow' });
      window.location.href = target;
    };

    const runGuard = async () => {
      // An impersonated CCN target's authData looks like a CUBE session, but
      // the staff member's browser has no CUBE session to check. Re-read on
      // every tick: impersonation starts and stops without a remount.
      if (impersonationUtils.isImpersonating()) {
        return;
      }
      if (!isCubeSession()) {
        return;
      }
      if (Date.now() - mountedAt.current < CUBE_GUARD_GRACE_MS) {
        return;
      }

      let alive = false;
      try {
        alive = await guardCubeSession();
      } catch {
        alive = false;
      }
      if (cancelled) return;

      if (alive) {
        failures.current = 0;
        return;
      }
      failures.current += 1;
      if (failures.current < CUBE_GUARD_FAILURES_TO_LOGOUT) {
        console.warn(`[CUBE SSO] guard failed (${failures.current}/${CUBE_GUARD_FAILURES_TO_LOGOUT})`);
        return;
      }
      expire('Your CUBEforall session expired. Please sign in again.');
    };

    void runGuard();
    const interval = window.setInterval(() => void runGuard(), CUBE_GUARD_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return null;
}
